/**
 * LessonQuizPanel — student-facing per-lesson practice quiz.
 *
 * Renders below the lesson body inside the Learn view. Fetches the lesson's
 * quiz (options-only view — the backend strips `correct_index` for students).
 * If a quiz exists, the student can answer and submit; results show pass/fail
 * plus score/total. Attempts are recorded in `lesson_quiz_attempts` for
 * future analytics.
 *
 * Follows the visual language of `ChapterAssignmentPanel` — ivory/gold accent
 * border, ClipboardList icon, subtle motion.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Brain, Loader2, CheckCircle2, XCircle, ChevronRight, RotateCcw,
} from "lucide-react";

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

export default function LessonQuizPanel({ lesson }) {
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.get(`/lessons/${lesson.id}/quiz`);
            setQuiz(data);
            setAnswers({});
            // Persist the "already passed" state so refreshes don't lose
            // the pass badge — the server tells us the student's best row.
            if (data?.already_passed) {
                setResult({
                    passed: true,
                    score: data.best_score ?? data.total,
                    total: data.total,
                    pass_mark: data.pass_mark,
                });
            }
        } catch {
            setQuiz(null);
        } finally {
            setLoading(false);
        }
    }, [lesson.id]);

    useEffect(() => { load(); }, [load]);

    const totalQ = quiz?.questions?.length || 0;
    const answeredCount = useMemo(
        () => Object.keys(answers).filter((k) => answers[k] !== undefined).length,
        [answers],
    );

    const submit = async () => {
        if (!quiz) return;
        if (answeredCount !== totalQ) {
            toast.error(`Please answer all ${totalQ} questions before submitting.`);
            return;
        }
        setSubmitting(true);
        try {
            const payload = { answers: quiz.questions.map((_, i) => answers[i]) };
            const { data } = await api.post(
                `/lessons/${lesson.id}/quiz/submit`, payload,
            );
            setResult(data);
            if (data.passed) {
                toast.success(`Well done — ${data.score}/${data.total} (${Math.round(100 * data.score / data.total)}%). You may continue to the next lesson.`);
                // Tell the Learn page to refresh so the sidebar unlocks
                // whatever depends on this lesson quiz being passed.
                window.dispatchEvent(new CustomEvent("cco:lesson-quiz-passed", {
                    detail: { lessonId: lesson.id },
                }));
            } else {
                toast.error(`${data.score}/${data.total} — need ${data.pass_mark}/${data.total} to pass (80%). Retry when ready.`);
            }
        } catch (e) {
            toast.error(fmtError(e, "Could not submit quiz"));
        } finally {
            setSubmitting(false);
        }
    };

    const retry = () => {
        setAnswers({});
        setResult(null);
    };

    if (loading) {
        return (
            <div className="my-6 p-6 rounded-xl border border-border bg-muted/10 text-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
        );
    }

    if (!quiz || !totalQ) return null;

    return (
        <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="my-6 rounded-2xl border border-primary/20 bg-white shadow-sm overflow-hidden"
            data-testid="lesson-quiz-panel"
        >
            <div className="p-5 border-b border-border bg-[#FBF5E8]/60 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-[#C79520]" />
                        <span className="text-[11px] uppercase tracking-[0.14em] text-[#C79520] font-semibold">
                            Lesson practice quiz
                        </span>
                    </div>
                    <h3 className="font-display text-2xl text-[#134B30] mt-1">
                        Check what you learned
                    </h3>
                    <div className="text-xs text-muted-foreground mt-1">
                        {totalQ} questions · Pass mark 80% ({Math.max(1, Math.ceil(totalQ * 0.8))}/{totalQ})
                    </div>
                </div>
                {result ? (
                    <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${
                            result.passed
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-rose-100 text-rose-800"
                        }`}
                        data-testid="lesson-quiz-result"
                    >
                        {result.passed ? (
                            <CheckCircle2 className="h-4 w-4" />
                        ) : (
                            <XCircle className="h-4 w-4" />
                        )}
                        {result.score}/{result.total}
                        {" — "}
                        {Math.round((result.score / result.total) * 100)}%
                    </div>
                ) : null}
            </div>

            <div className="p-5 space-y-6">
                {quiz.questions.map((q, qi) => (
                    <div key={qi} data-testid={`lesson-quiz-question-${qi}`}>
                        <div className="text-sm font-semibold text-foreground mb-2">
                            {qi + 1}. {q.text}
                        </div>
                        <div className="space-y-2">
                            {q.options.map((opt, oi) => {
                                const selected = answers[qi] === oi;
                                return (
                                    <button
                                        key={oi}
                                        type="button"
                                        onClick={() =>
                                            !result && setAnswers((s) => ({ ...s, [qi]: oi }))
                                        }
                                        disabled={!!result}
                                        className={`w-full text-left px-3 py-2 rounded border text-sm transition ${
                                            selected
                                                ? "border-[#134B30] bg-[#134B30]/5 text-[#134B30]"
                                                : "border-border hover:bg-muted/40"
                                        } ${result ? "cursor-default opacity-90" : ""}`}
                                        data-testid={`lesson-quiz-opt-${qi}-${oi}`}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <span
                                                className={`h-4 w-4 rounded-full border flex-shrink-0 ${
                                                    selected
                                                        ? "border-[#134B30] bg-[#134B30]"
                                                        : "border-border"
                                                }`}
                                            />
                                            <span>{opt}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}

                <div className="pt-3 border-t border-border flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-muted-foreground">
                        {result ? (
                            <>Attempt recorded. Try again to improve your score.</>
                        ) : (
                            <>{answeredCount} of {totalQ} answered</>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {result ? (
                            <Button
                                variant="outline"
                                onClick={retry}
                                className="gap-2"
                                data-testid="lesson-quiz-retry"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Try again
                            </Button>
                        ) : (
                            <Button
                                onClick={submit}
                                disabled={submitting || answeredCount !== totalQ}
                                className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-2"
                                data-testid="lesson-quiz-submit"
                            >
                                {submitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                                Submit quiz
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </motion.section>
    );
}
